import { User } from "lucide-react";
import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Spinner,
} from "@/components/common";
import { useProfile, useUpdateProfile } from "@/hooks/useSettings";
import type { UpdateProfilePayload } from "@/types/settings";

const languageOptions = [
  { value: "pl", label: "Polski" },
  { value: "en", label: "English" },
] as const;

export function ProfileTab() {
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();

  const [form, setForm] = useState<UpdateProfilePayload | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize form from fetched data
  const formData = form ?? {
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    phone: profile?.phone ?? "",
    language: profile?.language ?? ("pl" as const),
  };

  function handleChange(field: keyof UpdateProfilePayload, value: string) {
    setSuccess(false);
    setForm({ ...formData, [field]: value });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateProfile.mutate(formData, {
      onSuccess: () => {
        setSuccess(true);
        setForm(null);
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-adara-50 p-2 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300">
          <User size={20} aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-main">Dane osobowe</h3>
          <p className="mt-1 text-xs text-dim">
            Zaktualizuj swoje dane kontaktowe i preferencje językowe
          </p>
        </div>
      </div>

      {success && (
        <Alert
          variant="success"
          className="mt-4"
          onDismiss={() => {
            setSuccess(false);
          }}
        >
          Profil został zaktualizowany
        </Alert>
      )}

      {updateProfile.isError && (
        <Alert variant="error" className="mt-4">
          Nie udało się zapisać zmian
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Imię"
            value={formData.firstName}
            onChange={(e) => {
              handleChange("firstName", e.target.value);
            }}
            required
          />
          <Input
            label="Nazwisko"
            value={formData.lastName}
            onChange={(e) => {
              handleChange("lastName", e.target.value);
            }}
            required
          />
        </div>

        <Input
          label="Telefon"
          type="tel"
          value={formData.phone}
          onChange={(e) => {
            handleChange("phone", e.target.value);
          }}
          placeholder="+48 500 100 200"
        />

        <Select
          label="Język"
          options={[...languageOptions]}
          value={formData.language}
          onChange={(val) => {
            handleChange("language", val);
          }}
        />

        <div className="flex justify-end border-t border-edge-soft pt-4">
          <Button type="submit" isLoading={updateProfile.isPending}>
            Zapisz zmiany
          </Button>
        </div>
      </form>
    </Card>
  );
}
